"use client";
import getGraphqlEndpoint from "@workspace/casino_core/useSubscription/getGraphqlEndpoint";
import CustomNotificationEvent from "@workspace/casino_pages/legacyLayer/client/events/CustomNotificationEvent";
import isWebComponentGame from "@workspace/casino_pages/legacyLayer/shared/utils/isWebComponentGame";
import { getCookie } from "@workspace/cookies/browser";
import DeviceType from "@workspace/nextjs_utils/src/DeviceType";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import closeNotification from "@/server/components/NotificationWorker/NotificationWorkerHandler/helpers/closeNotification";
import isNotificationClosed from "@/server/components/NotificationWorker/NotificationWorkerHandler/helpers/isNotificationClosed";
import showNotification from "@/server/components/NotificationWorker/NotificationWorkerHandler/showNotification";

// The token is only handed to the worker, so it is read on the client.
type Props = Omit<WorkerContext, "endpoint" | "token"> & {
  initialNotifications?: WorkerNotification[];
};

function canShowNotifications(
  pathname: string | undefined,
  deviceType: DeviceType,
): boolean {
  return !isWebComponentGame(pathname) || deviceType === DeviceType.DESKTOP;
}

const NotificationWorkerHandler: FC<Props> = (props) => {
  const pathname = usePathname();
  useEffect(() => {
    const handle = pathname?.split("/").pop();
    const context: WorkerContext = {
      ...props,
      token: getCookie("token"),
      endpoint: getGraphqlEndpoint(),
    };
    const worker = new Worker(new URL("./notificationWorker", import.meta.url));

    worker.onmessage = (event: MessageEvent) => {
      if (!canShowNotifications(handle, props.deviceType)) {
        return;
      }

      const notification = event.data as WorkerNotification;
      if (isNotificationClosed(notification)) {
        worker.postMessage({ type: "sendNext" });
        return;
      }
      const onClose = () => {
        closeNotification(notification);
        worker.postMessage({ type: "sendNext" });
      };

      showNotification({ notification, context, onClose });
    };
    worker.postMessage({ type: "init", context });
    props.initialNotifications?.forEach((notification) => {
      worker.postMessage({ notification, type: "add" });
    });

    const customEvent = new CustomNotificationEvent((notification) =>
      worker.postMessage({ notification, type: "add" }),
    );
    customEvent.subscribe();

    return () => {
      worker.terminate();
      customEvent.unsubscribe();
    };
  }, [pathname]);

  return null;
};

export default NotificationWorkerHandler;
